import { ErrorMessage } from 'components/error-message';
import {
  applyBlockSearchSuggestion,
  BlockSearchSuggestion,
  describeBlockSearch,
  getBlockSearchSuggestions,
  parseBlockSearch,
} from 'containers/composer/blocks/blockSearch';
import { KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Popup, PopupContent, PopupHeader } from 'semantic-ui-react';
import styled from 'styled-components';

const EMPTY_SEARCH = '';

// Full width, so longer queries with several filters stay readable
const SearchColumn = styled.div`
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const SuggestionList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 5;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 0.28571429rem;
  box-shadow: 0 6px 18px rgba(34, 36, 38, 0.18);
  max-height: 320px;
  overflow-y: auto;
`;

const SuggestionItem = styled.div<{ $active?: boolean }>`
  padding: 10px 12px;
  background: ${({ $active }) => ($active ? 'rgba(33, 133, 208, 0.08)' : '#fff')};
  cursor: pointer;
  font-size: 0.95em;

  &:hover {
    background: rgba(33, 133, 208, 0.08);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(34, 36, 38, 0.08);
  }
`;

const SuggestionMeta = styled.div`
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.82em;
  margin-top: 2px;
`;

const EXPLANATION_LINE_HEIGHT = '1.4em';

// Reserves the explanation's line, so the results do not move when it appears
const BelowSearch = styled.div`
  margin-top: 6px;
  min-height: calc(${EXPLANATION_LINE_HEIGHT} * 0.92);
`;

const ExplanationBox = styled.div`
  position: relative;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.92em;
  line-height: ${EXPLANATION_LINE_HEIGHT};

  &:focus {
    outline: none;
  }
`;

// One line; a longer explanation ends in an ellipsis
const SearchExplanation = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// The full explanation, laid over the content below on hover or focus, so nothing moves
const FullExplanation = styled.div`
  display: none;
  position: absolute;
  top: -5px;
  left: -9px;
  right: -9px;
  z-index: 4;
  padding: 4px 8px;
  overflow-wrap: anywhere;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 0.28571429rem;
  box-shadow: 0 6px 18px rgba(34, 36, 38, 0.18);

  ${ExplanationBox}:hover > &,
  ${ExplanationBox}:focus > & {
    display: block;
  }
`;

/** Plain-language description of the query, or '' when the query is empty or invalid. */
function explainSearch(searchTerm: string): string {
  try {
    return describeBlockSearch(parseBlockSearch(searchTerm).filter);
  } catch {
    return '';
  }
}

function suggestionKey(suggestion: BlockSearchSuggestion) {
  return `${suggestion.kind}:${suggestion.text}`;
}

function suggestionMeta(suggestion: BlockSearchSuggestion, isActive: boolean) {
  if (suggestion.kind === 'value') {
    return isActive ? 'Common variation - press Enter to insert' : 'Common variation';
  }

  if (suggestion.kind === 'boolean') {
    return isActive ? 'Press Enter to insert' : 'Click or use the arrow keys to select';
  }

  return isActive ? 'Press Enter to insert filter' : 'Click or use the arrow keys to select';
}

// Keep the help popup inside the viewport; it opens from the right edge of the search box.
const HELP_POPUP_STYLE = { maxWidth: 'min(960px, calc(100vw - 2rem))' };

// Two columns when the popup has room for them, so the text stays readable while using the extra width
const HelpContent = styled.div`
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.76);
  columns: 2 400px;
  column-gap: 2rem;

  p {
    margin: 0 0 8px;
  }

  ul {
    margin: 0 0 10px 18px;
    padding: 0;
  }

  p,
  li {
    break-inside: avoid;
  }

  li {
    margin-bottom: 6px;
  }

  code {
    background: rgba(34, 36, 38, 0.06);
    border-radius: 3px;
    padding: 1px 4px;
  }
`;

interface Props {
  onSearchChange: (searchTerm: string) => void;
  searchError?: Error | null;
  searchTerm: string;
}

let searchInstanceCounter = 0;

export function BlockOverviewSearch({ onSearchChange, searchError, searchTerm }: Props) {
  const [idPrefix] = useState(() => `block-search-${++searchInstanceCounter}`);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const availableSuggestions = useMemo(() => getBlockSearchSuggestions(searchTerm), [searchTerm]);
  const suggestions = isOpen ? availableSuggestions : [];
  const activeIndex = highlightedIndex < suggestions.length ? highlightedIndex : -1;
  const showSuggestions = suggestions.length > 0;

  const listId = `${idPrefix}-suggestions`;
  const errorId = `${idPrefix}-error`;
  const explanationId = `${idPrefix}-explanation`;
  const optionId = (index: number) => `${idPrefix}-option-${index}`;

  // Parsed immediately (not debounced) so the explanation follows typing; the debounced error takes precedence.
  const explanation = useMemo(() => explainSearch(searchTerm), [searchTerm]);
  const showExplanation = !searchError && explanation.length > 0;
  let describedBy: string | undefined;
  if (searchError) {
    describedBy = errorId;
  } else if (showExplanation) {
    describedBy = explanationId;
  }

  // Whether the explanation is cut off, measured again when it or the available width changes
  const explanationRef = useRef<HTMLDivElement>(null);
  const [isExplanationTruncated, setIsExplanationTruncated] = useState(false);
  useLayoutEffect(() => {
    const element = explanationRef.current;
    if (!element) {
      return undefined;
    }

    const measure = () => setIsExplanationTruncated(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [explanation, showExplanation]);

  // Keep the highlighted suggestion visible when the list scrolls
  useEffect(() => {
    if (activeIndex >= 0) {
      document.getElementById(`${idPrefix}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, idPrefix]);

  const closeSuggestions = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const inputRef = useRef<Input>(null);

  // Escape must only close the suggestion list, not an enclosing semantic Modal (e.g. the block picker). Semantic's Portal
  // listens for Escape on `document` in the capture phase, i.e. before React's onKeyDown runs, so a React stopPropagation
  // is too late. A capturing listener on `window` runs first and can stop the event before it reaches the Portal.
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showSuggestions) {
      return undefined;
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || !(event.target instanceof Node) || !inputWrapperRef.current?.contains(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      setHighlightedIndex(-1);
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [showSuggestions]);

  const applySuggestion = (suggestion: BlockSearchSuggestion) => {
    const nextSearchTerm = applyBlockSearchSuggestion(searchTerm, suggestion);
    onSearchChange(nextSearchTerm);
    // Keep the list open when the inserted text has suggestions of its own, e.g. the values after picking variation:
    setIsOpen(getBlockSearchSuggestions(nextSearchTerm).length > 0);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = availableSuggestions.length;

    if (event.key === 'ArrowDown' && count > 0) {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(showSuggestions ? (activeIndex + 1) % count : 0);
    } else if (event.key === 'ArrowUp' && showSuggestions) {
      event.preventDefault();
      setHighlightedIndex(activeIndex <= 0 ? count - 1 : activeIndex - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      applySuggestion(suggestions[activeIndex]);
    }
  };

  return (
    <SearchColumn>
      <InputWrapper ref={inputWrapperRef}>
        <Input
          ref={inputRef}
          fluid
          size="large"
          value={searchTerm}
          placeholder="Search blocks, e.g. heart tag:hospital (archived blocks are hidden unless you use archived:)"
          onChange={(e) => {
            onSearchChange(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={closeSuggestions}
          label="Search"
          autoComplete="off"
          aria-label="Search blocks"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls={showSuggestions ? listId : undefined}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-invalid={!!searchError}
          aria-describedby={describedBy}
          action={
            <>
              <Button
                icon="times"
                aria-label="Clear search"
                disabled={!searchTerm || searchTerm === EMPTY_SEARCH}
                onClick={() => {
                  onSearchChange(EMPTY_SEARCH);
                  closeSuggestions();
                  inputRef.current?.focus();
                }}
              />
              <Popup trigger={<Button icon="question" aria-label="Search help" />} position="bottom right" hoverable flowing style={HELP_POPUP_STYLE}>
                <PopupHeader>Search help and examples</PopupHeader>
                <PopupContent>
                  <HelpContent>
                    <p>
                      Supported filters: <code>name:</code>, <code>tag:</code>, <code>manuscript:</code>, <code>description:</code>,{' '}
                      <code>variation:</code>, <code>speak-variation:</code>, <code>video-variation:</code>, <code>platform-ready:</code>,{' '}
                      <code>archived:</code>.
                    </p>
                    <p>
                      A filter value <strong>without quotes is a contains match</strong>: <code>tag:hospital</code> also finds the tag{' '}
                      <code>hospital-arrival</code>, and <code>description:heart</code> finds any description containing <code>heart</code>. A value{' '}
                      <strong>in quotes is an exact match</strong> (the whole value, ignoring case): <code>tag:&quot;hospital&quot;</code>,{' '}
                      <code>description:&quot;heart valve&quot;</code>.
                    </p>
                    <ul>
                      <li>
                        <code>tag:&quot;*heart valve*&quot;</code> (quotes with <code>*</code> on both sides) is a contains match for several words.{' '}
                        <code>name:*operation*</code> without quotes is the same as <code>name:operation</code>.
                      </li>
                      <li>
                        Plain words next to each other are <strong>searched as one phrase in every field</strong> (a contains match):{' '}
                        <code>tag:hospital arrival at</code> finds blocks with a tag containing <code>hospital</code> and the text{' '}
                        <code>arrival at</code> in any field. To search for words separately, put <code>AND</code> between them or use separate
                        filters: <code>arrival AND at</code> finds blocks containing both words anywhere. <code>&quot;arrival at&quot;</code> in
                        quotes also searches for that phrase in every field.
                      </li>
                      <li>
                        <code>variation:female</code> searches the variations of every speak and video; <code>speak-variation:</code> and{' '}
                        <code>video-variation:</code> only look at speaks or videos, e.g. <code>video-variation:&quot;male anatomy&quot;</code>.
                        Common variations are suggested while you type the value, and are inserted in quotes (an exact match).{' '}
                        <code>platform-ready:</code> and <code>archived:</code> suggest <code>yes</code> and <code>no</code>.
                      </li>
                      <li>
                        <code>-tag:hospital</code> or <code>-(tag:a OR tag:b)</code> excludes matches.
                      </li>
                      <li>
                        <code>(tag:hospital OR tag:emergencyroom) AND platform-ready:yes</code> combines grouped OR logic with another filter.{' '}
                        <code>AND</code>/<code>OR</code> are not case-sensitive; quote them (<code>&quot;or&quot;</code>) to search for the word
                        itself.
                      </li>
                      <li>
                        Archived blocks are hidden unless the query uses <code>archived:</code>. <code>archived:yes</code> shows only archived blocks;{' '}
                        <code>(archived:yes OR archived:no)</code> shows all blocks.
                      </li>
                      <li>
                        Use <code>\&quot;</code>, <code>\\</code> and <code>\*</code> for quotes, backslashes and literal stars inside quotes.
                      </li>
                    </ul>
                    <p>The line below the search box describes the current search in plain words.</p>
                  </HelpContent>
                </PopupContent>
              </Popup>
            </>
          }
        />
        {showSuggestions && (
          <SuggestionList id={listId} role="listbox" aria-label="Search suggestions">
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestionKey(suggestion)}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                $active={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applySuggestion(suggestion)}
              >
                <div>{suggestion.text}</div>
                <SuggestionMeta>{suggestionMeta(suggestion, index === activeIndex)}</SuggestionMeta>
              </SuggestionItem>
            ))}
          </SuggestionList>
        )}
      </InputWrapper>
      <BelowSearch>
        {/* Errors are announced (they appear after a debounce); the explanation is only described, as it changes with every keystroke */}
        <div id={errorId} aria-live="polite">
          <ErrorMessage error={searchError} />
        </div>
        {/* Focusable when cut off, so the full text can also be read from the keyboard */}
        <ExplanationBox tabIndex={showExplanation && isExplanationTruncated ? 0 : undefined}>
          <SearchExplanation ref={explanationRef} id={explanationId}>
            {showExplanation ? explanation : null}
          </SearchExplanation>
          {showExplanation && isExplanationTruncated && <FullExplanation aria-hidden>{explanation}</FullExplanation>}
        </ExplanationBox>
      </BelowSearch>
    </SearchColumn>
  );
}
